import i18n from 'helpers/i18n';
import messages from './messages';
import { useState } from 'react';
import { rejectVideo } from 'actions/video-obs/double-coders';
import { Dialog, Button, TextArea } from '@teachstonellc/component-library';

interface RejectVideoButtonProps {
  /** The text to be displayed on the reject button. */
  children: string;

  /** The ID of the video upload to be rejected. */
  videoUploadId: number;

  /** Callback function to be called after the video is rejected. */
  onVideoRejected: () => void;

  /** The variant of the button, which determines its styling. */
  variant: 'primary' | 'secondary' | 'aux';
}

export function RejectVideoButton({
  children,
  videoUploadId,
  onVideoRejected,
  variant
}: RejectVideoButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  async function handleRejectVideo() {
    setIsLoading(true);
    await rejectVideo(videoUploadId, { reason: rejectReason });
    setIsLoading(false);
    setIsRejecting(false);
    onVideoRejected();
  }

  return (
    <>
      <Dialog
        open={isRejecting}
        title={i18n.ft(messages.rejectButton.rejectReasonTitle)}
        onOpenChange={open => setIsRejecting(open)}
        actionButton={
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              disabled={isLoading}
              onClick={() => setIsRejecting(false)}
            >
              {i18n.ft(messages.rejectButton.cancelButton)}
            </Button>
            <Button
              variant="primary"
              loading={isLoading}
              onClick={handleRejectVideo}
              disabled={isLoading || rejectReason.trim() === ''}
            >
              {i18n.ft(messages.rejectButton.rejectButton)}
            </Button>
          </div>
        }
      >
        <TextArea
          label={i18n.ft(messages.rejectButton.rejectReasonLabel)}
          value={rejectReason}
          onChange={e => setRejectReason(e.target.value)}
        />
      </Dialog>

      <Button
        fullWidth
        size="large"
        variant={variant}
        disabled={isLoading}
        onClick={() => setIsRejecting(true)}
      >
        {children}
      </Button>
    </>
  );
}
